import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';
import { isCanceledRequest } from '../utils/listSearchRequest';

const LOG_CONTEXT = 'order_request_client';

/**
 * Build a compact, non-sensitive summary for client/API errors.
 *
 * @param {Object|Array|string|null} errPayload
 * @param {Object} [error]
 * @returns {Object}
 */
const summarizeOrderRequestError = (errPayload, error) => ({
  errorType: Array.isArray(errPayload) ? 'array' : typeof errPayload,
  errorMessage:
    typeof errPayload === 'string'
      ? errPayload
      : error?.message || 'Request failed',
  fieldCount:
    errPayload && typeof errPayload === 'object' && !Array.isArray(errPayload)
      ? Object.keys(errPayload).length
      : 0,
});

/**
 * Flatten DRF validation errors from order request endpoints.
 *
 * @param {Object|Array|string|null} errPayload
 * @returns {string[]}
 */
export const formatOrderRequestErrors = errPayload => {
  if (errPayload == null) {
    return ['Request failed. Please try again.'];
  }
  if (typeof errPayload === 'string') {
    return [errPayload];
  }
  if (Array.isArray(errPayload)) {
    return errPayload.map(item => String(item));
  }
  const messages = [];
  Object.entries(errPayload).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(v => messages.push(`${key}: ${v}`));
    } else if (typeof value === 'object') {
      messages.push(`${key}: ${JSON.stringify(value)}`);
    } else {
      messages.push(String(value));
    }
  });
  return messages.length ? messages : ['Request failed. Please try again.'];
};

/**
 * Normalize global order contact settings payload for UI.
 *
 * @param {Object|null} data
 * @returns {{contact_name:string,contact_email:string,updated_at:(string|null),updated_by:(string|null)}}
 */
export const normalizeOrderContactSettings = data => ({
  contact_name: data?.contact_name ?? '',
  contact_email: data?.contact_email ?? '',
  updated_at: data?.updated_at ?? null,
  updated_by: data?.updated_by ?? null,
});

/**
 * Display name for the user who last saved lab contact settings.
 * The API sends `{ id, username, full_name, email }`, not a string.
 *
 * @param {string|Object|null|undefined} updatedBy
 * @returns {string}
 */
export const formatOrderContactUpdatedBy = updatedBy => {
  if (!updatedBy) return '';
  if (typeof updatedBy === 'string') return updatedBy.trim();
  const fullName = String(updatedBy.full_name || '').trim();
  const username = String(updatedBy.username || '').trim();
  return fullName || username;
};

/**
 * Lab-contact `updated_at` is an ISO datetime. `formatDateStr` only splits
 * `yyyy-mm-dd`, so a full timestamp would leave the time inside the day.
 *
 * @param {string|null|undefined} updatedAt
 * @returns {string} `dd/mm/yyyy HH:mm` in local time, or '' when missing
 */
export const formatOrderContactUpdatedAt = updatedAt => {
  if (!updatedAt) return '';
  const parsed = new Date(updatedAt);
  if (Number.isNaN(parsed.getTime())) return '';
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = parsed.getFullYear();
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Map DRF errors for global order contact settings form.
 *
 * @param {Object|Array|string|null} errPayload
 * @returns {{contact_name:string[],contact_email:string[],non_field_errors:string[]}}
 */
export const mapOrderContactSettingsErrors = errPayload => {
  const empty = {
    contact_name: [],
    contact_email: [],
    non_field_errors: [],
  };
  if (!errPayload) return empty;
  if (typeof errPayload === 'string') {
    return { ...empty, non_field_errors: [errPayload] };
  }
  if (Array.isArray(errPayload)) {
    return {
      ...empty,
      non_field_errors: errPayload.map(v => String(v)),
    };
  }
  return {
    contact_name: Array.isArray(errPayload?.contact_name)
      ? errPayload.contact_name.map(v => String(v))
      : [],
    contact_email: Array.isArray(errPayload?.contact_email)
      ? errPayload.contact_email.map(v => String(v))
      : [],
    non_field_errors: Array.isArray(errPayload?.non_field_errors)
      ? errPayload.non_field_errors.map(v => String(v))
      : [],
  };
};

/**
 * @param {Object|null} line
 * @returns {Object}
 */
function normalizeOrderRequestLine(line) {
  if (!line) return {};
  return {
    id: line.id,
    product_id: line.product_id,
    product_name: line.product_name ?? '',
    cat_num: line.cat_num ?? '',
    supplier_id: line.supplier_id,
    supplier_name: line.supplier_name ?? '',
    quantity: line.quantity,
    notes: line.notes ?? '',
  };
}

/**
 * Normalize full order request detail for UI.
 *
 * @param {Object|null} data
 * @returns {Object|null}
 */
export const normalizeOrderRequestDetail = data => {
  if (!data) return null;
  const lines = Array.isArray(data.lines)
    ? data.lines.map(normalizeOrderRequestLine)
    : [];
  const primary = lines[0] || {};
  return {
    id: data.id,
    request_ref: data.request_ref ?? '',
    status: data.status ?? '',
    requested_by: data.requested_by,
    requested_by_name: data.requested_by_name ?? '',
    project: data.project,
    project_name: data.project_name ?? '',
    notes: data.notes ?? '',
    contact_name: data.contact_name ?? '',
    contact_email: data.contact_email ?? '',
    created_at: data.created_at,
    updated_at: data.updated_at,
    fulfilled_at: data.fulfilled_at,
    fulfilled_by: data.fulfilled_by,
    fulfilled_by_name: data.fulfilled_by_name ?? '',
    linked_order: data.linked_order,
    lines,
    primary_supplier_id:
      data.primary_supplier_id ?? primary.supplier_id ?? null,
    primary_supplier_name:
      data.primary_supplier_name ?? primary.supplier_name ?? '',
  };
};

/**
 * Normalize list row; derive primary supplier from line or list fields.
 *
 * @param {Object} row
 * @returns {Object}
 */
export const normalizeOrderRequestListRow = row => {
  const lines = Array.isArray(row.lines)
    ? row.lines.map(normalizeOrderRequestLine)
    : [];
  const primary = lines[0] || {};
  return {
    ...row,
    lines,
    contact_name: row.contact_name ?? '',
    contact_email: row.contact_email ?? '',
    primary_supplier_id:
      row.primary_supplier_id ?? primary.supplier_id ?? null,
    primary_supplier_name:
      row.primary_supplier_name ??
      primary.supplier_name ??
      row.supplier_name ??
      '',
    line_count: row.line_count ?? lines.length,
    product_names: row.product_names ?? lines.map(l => l.product_name),
  };
};

/**
 * Group order request list rows by primary supplier for procurement inbox.
 *
 * @param {Array<Object>} rows
 * @returns {Array<{ supplierId: *, supplierName: string, requests: Array }>}
 */
export const groupOrderRequestsBySupplier = rows => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach(raw => {
    const row = normalizeOrderRequestListRow(raw);
    const key =
      row.primary_supplier_id != null
        ? String(row.primary_supplier_id)
        : row.primary_supplier_name || 'unknown';
    if (!map.has(key)) {
      map.set(key, {
        supplierId: row.primary_supplier_id,
        supplierName: row.primary_supplier_name || 'Unknown supplier',
        requests: [],
      });
    }
    map.get(key).requests.push(row);
  });
  return Array.from(map.values()).sort((a, b) =>
    (a.supplierName || '').localeCompare(b.supplierName || '')
  );
};

/**
 * POST /v1/products/{productId}/request_order/
 *
 * Inventory order request create. Contact is supplied as `contact_user_id`;
 * backend derives `contact_name` / `contact_email` from the selected user.
 *
 * @async
 * @param {string} token
 * @param {number|string} productId
 * @param {Object} payload
 * @param {number} payload.quantity - Required integer >= 1.
 * @param {number} [payload.contact_user_id] - Optional user pk for order contact.
 * @param {number} [payload.project] - Optional project pk.
 * @param {string} [payload.notes] - Optional notes.
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const requestOrderFromProduct = async (token, productId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'requestOrderFromProduct start', {
      productId,
      quantity: payload?.quantity,
      contactUserId: payload?.contact_user_id,
    });
    const response = await axios.post(
      `${BACKEND_URL}products/${productId}/request_order/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = normalizeOrderRequestDetail(response.data);
    logInfo(LOG_CONTEXT, 'requestOrderFromProduct success', {
      productId,
      requestRef: data?.request_ref,
      orderRequestId: data?.id,
    });
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'requestOrderFromProduct failed', {
      productId,
      ...summarizeOrderRequestError(errPayload, error),
    });
    return {
      success: false,
      error: errPayload,
      messages: formatOrderRequestErrors(errPayload),
    };
  }
};

/**
 * POST /v1/order-requests/ (batch; no v1 UI).
 *
 * @async
 * @param {string} token
 * @param {Object} payload
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const createOrderRequest = async (token, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'createOrderRequest start', {
      lineCount: Array.isArray(payload?.lines) ? payload.lines.length : 0,
    });
    const response = await axios.post(
      `${BACKEND_URL}order-requests/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = normalizeOrderRequestDetail(response.data);
    logInfo(LOG_CONTEXT, 'createOrderRequest success', {
      requestRef: data?.request_ref,
    });
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'createOrderRequest failed', {
      ...summarizeOrderRequestError(errPayload, error),
    });
    return {
      success: false,
      error: errPayload,
      messages: formatOrderRequestErrors(errPayload),
    };
  }
};

/**
 * GET /v1/order-requests/ (paginated).
 *
 * @async
 * @param {string} token
 * @param {function} setResults
 * @param {Object} options
 * @returns {Promise<{success:boolean,nextPage?:string,reachedEnd?:boolean,error?:*}>}
 */
export const getOrderRequests = async (token, setResults, options = {}) => {
  const {
    status = '',
    requestedBy = '',
    project = '',
    productId = '',
    createdAfter = '',
    createdBefore = '',
    searchInput = '',
    nextPage = null,
    signal = undefined,
    updateState = true,
  } = options;

  let url = nextPage || `${BACKEND_URL}order-requests/?page_num=1`;

  if (!nextPage) {
    if (searchInput) url += `&search=${encodeURIComponent(searchInput)}`;
    if (status) url += `&status=${encodeURIComponent(status)}`;
    if (requestedBy) url += `&requested_by=${requestedBy}`;
    if (project) url += `&project=${project}`;
    if (productId) url += `&product_id=${productId}`;
    if (createdAfter) url += `&created_after=${encodeURIComponent(createdAfter)}`;
    if (createdBefore) {
      url += `&created_before=${encodeURIComponent(createdBefore)}`;
    }
  }

  try {
    logInfo(LOG_CONTEXT, 'getOrderRequests start', {
      nextPageProvided: Boolean(nextPage),
      status: status || undefined,
      requestedBy: requestedBy || undefined,
      project: project || undefined,
      productId: productId || undefined,
      hasSearchInput: searchInput.length >= 2,
      hasCreatedAfter: Boolean(createdAfter),
      hasCreatedBefore: Boolean(createdBefore),
    });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
      signal,
    });
    const nextCursor = response.data.next;
    const results = (response.data.results || []).map(
      normalizeOrderRequestListRow
    );
    const writeState =
      updateState !== false && typeof setResults === 'function';

    if (writeState) {
      if (!nextPage) {
        setResults(results);
      } else {
        setResults(prev => {
          const ids = new Set(prev.map(r => r.id));
          const added = results.filter(r => !ids.has(r.id));
          return [...prev, ...added];
        });
      }
    }
    logInfo(LOG_CONTEXT, 'getOrderRequests success', {
      reachedEnd: !nextCursor,
      count: results.length,
      hasNextPage: Boolean(nextCursor),
      updateState: writeState,
    });
    return {
      success: true,
      results,
      reachedEnd: !nextCursor,
      nextPage: nextCursor || null,
    };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'getOrderRequests failed', {
      ...summarizeOrderRequestError(errPayload, error),
      nextPageProvided: Boolean(nextPage),
    });
    return { success: false, error: errPayload };
  }
};

/**
 * GET /v1/order-requests/{id}/
 *
 * @async
 * @param {string} token
 * @param {number|string} id
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const getOrderRequestDetail = async (token, id) => {
  try {
    logInfo(LOG_CONTEXT, 'getOrderRequestDetail start', { id });
    const response = await axios.get(`${BACKEND_URL}order-requests/${id}/`, {
      headers: { Authorization: `Token ${token}` },
    });
    logInfo(LOG_CONTEXT, 'getOrderRequestDetail success', {
      id,
      requestRef: response.data?.request_ref,
    });
    return {
      success: true,
      data: normalizeOrderRequestDetail(response.data),
    };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'getOrderRequestDetail failed', {
      id,
      ...summarizeOrderRequestError(errPayload, error),
    });
    return {
      success: false,
      error: errPayload,
      messages: formatOrderRequestErrors(errPayload),
    };
  }
};

/**
 * PATCH /v1/order-requests/{id}/
 *
 * @async
 * @param {string} token
 * @param {number|string} id
 * @param {Object} payload
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const patchOrderRequest = async (token, id, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'patchOrderRequest start', { id, status: payload?.status });
    const response = await axios.patch(
      `${BACKEND_URL}order-requests/${id}/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = normalizeOrderRequestDetail(response.data);
    logInfo(LOG_CONTEXT, 'patchOrderRequest success', { id, status: data?.status });
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'patchOrderRequest failed', {
      id,
      ...summarizeOrderRequestError(errPayload, error),
    });
    return {
      success: false,
      error: errPayload,
      messages: formatOrderRequestErrors(errPayload),
    };
  }
};

/**
 * PATCH /v1/order-requests/{id}/update_contact/
 *
 * @async
 * @param {string} token
 * @param {number|string} id
 * @param {{contact_name:string,contact_email:string}} payload
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const updateOrderRequestContact = async (token, id, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'updateOrderRequestContact start', { id });
    const response = await axios.patch(
      `${BACKEND_URL}order-requests/${id}/update_contact/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = normalizeOrderRequestDetail(response.data);
    logInfo(LOG_CONTEXT, 'updateOrderRequestContact success', {
      id,
      contact_name: data?.contact_name,
      contact_email: data?.contact_email,
    });
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'updateOrderRequestContact failed', {
      id,
      ...summarizeOrderRequestError(errPayload, error),
    });
    return {
      success: false,
      error: errPayload,
      messages: formatOrderRequestErrors(errPayload),
    };
  }
};

/**
 * GET /v1/order-contact-settings/
 *
 * @async
 * @param {string} token
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const getOrderContactSettings = async token => {
  try {
    logInfo(LOG_CONTEXT, 'getOrderContactSettings start');
    const response = await axios.get(`${BACKEND_URL}order-contact-settings/`, {
      headers: { Authorization: `Token ${token}` },
    });
    const data = normalizeOrderContactSettings(response.data);
    logInfo(LOG_CONTEXT, 'getOrderContactSettings success', {
      hasName: Boolean(data.contact_name),
      hasEmail: Boolean(data.contact_email),
    });
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'getOrderContactSettings failed', {
      ...summarizeOrderRequestError(errPayload, error),
    });
    return {
      success: false,
      error: errPayload,
      messages: formatOrderRequestErrors(errPayload),
    };
  }
};

/**
 * PATCH /v1/order-contact-settings/
 *
 * @async
 * @param {string} token
 * @param {{contact_name:string,contact_email:string}} payload
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const updateOrderContactSettings = async (token, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'updateOrderContactSettings start', {
      hasName: Boolean(payload?.contact_name),
      hasEmail: Boolean(payload?.contact_email),
    });
    const response = await axios.patch(
      `${BACKEND_URL}order-contact-settings/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = normalizeOrderContactSettings(response.data);
    logInfo(LOG_CONTEXT, 'updateOrderContactSettings success', {
      hasName: Boolean(data.contact_name),
      hasEmail: Boolean(data.contact_email),
    });
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'updateOrderContactSettings failed', {
      ...summarizeOrderRequestError(errPayload, error),
    });
    return {
      success: false,
      error: errPayload,
      messages: formatOrderRequestErrors(errPayload),
    };
  }
};
